/**
 * 指挥端对话：软删一条消息（入站 tracker_messages / 外发 tracker_commands）。
 * 服务端不可用时仍返回 ok:false，由 UI 做本地隐藏。
 */
import apiFetch from '../apiFetch';

/**
 * @param {{ id?: string, direction?: string, command_id?: number, batch_id?: string, message_id?: string }} item
 */
export function resolveDeleteTarget(item) {
  if (!item) return null;
  const direction = item.direction === 'out' ? 'out' : 'in';
  if (direction === 'out') {
    const commandId = item.command_id != null ? Number(item.command_id) : null;
    const batchId = item.batch_id ? String(item.batch_id) : null;
    // 历史外发 id 形如 tc_<id>
    let fromTc = null;
    if (!commandId && typeof item.id === 'string' && /^tc_\d+$/i.test(item.id)) {
      fromTc = Number(item.id.slice(3));
    }
    const cid = Number.isFinite(commandId) && commandId > 0
      ? commandId
      : (Number.isFinite(fromTc) && fromTc > 0 ? fromTc : null);
    if (!cid && !batchId) return null;
    return { direction: 'out', command_id: cid, batch_id: batchId, id: item.id };
  }
  const messageId = item.message_id || item.id;
  if (!messageId) return null;
  return { direction: 'in', message_id: String(messageId), id: item.id };
}

export async function deleteChatMessage(item) {
  const target = resolveDeleteTarget(item);
  if (!target) {
    const err = new Error('无法识别该消息，仅本地移除');
    err.code = 'unresolvable';
    throw err;
  }
  const res = await apiFetch('/api/tracker/messages/delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(target)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const err = new Error(data.error || data.message || `HTTP ${res.status}`);
    err.code = data.code || 'delete_failed';
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/** 本地会话隐藏 id 集合（结束任务 / 删消息） */
const HIDDEN_KEY = 'zhxt_command_chat_hidden_ids_v1';

export function loadHiddenChatIds() {
  try {
    const raw = localStorage.getItem(HIDDEN_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch (_) {
    return new Set();
  }
}

export function persistHiddenChatIds(set) {
  try {
    const arr = Array.from(set || []).slice(-500);
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(arr));
  } catch (_) { /* ignore */ }
}

export function hideChatMessageLocally(id) {
  if (!id) return loadHiddenChatIds();
  const set = loadHiddenChatIds();
  set.add(String(id));
  if (arguments.length > 1 && arguments[1]) set.add(String(arguments[1]));
  persistHiddenChatIds(set);
  return set;
}

export function clearHiddenChatIds() {
  try { localStorage.removeItem(HIDDEN_KEY); } catch (_) { /* ignore */ }
}

export function filterHiddenChatMessages(list, hiddenSet) {
  const hidden = hiddenSet || loadHiddenChatIds();
  if (!hidden.size) return Array.isArray(list) ? list : [];
  return (Array.isArray(list) ? list : []).filter((m) => {
    if (!m) return false;
    if (m.id && hidden.has(String(m.id))) return false;
    if (m.batch_id && hidden.has(`batch:${m.batch_id}`)) return false;
    if (m.command_id != null && hidden.has(`cmd:${m.command_id}`)) return false;
    return true;
  });
}
